import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { APP_CONFIG, APP_CONSTANTS } from "@app/constants";
import { OauthService } from "@app/services/oauth.service";
import { MatDialog } from "@angular/material/dialog";
import { interval, Subscription, take } from "rxjs";
import { Router } from "@angular/router";
import { ErrorDialogService } from "@app/services/error-dialog.service";
import { HeaderService } from "@app/components/header/header.service";

const SSO_LOGIN_ATTEMPTS_KEY = "sso_login_attempts";

const MAX_LOGIN_ATTEMPTS = 4;
const AUTH_DELAY_MS = 10000;

const PROGRESS_MAX = 95;
const PROGRESS_STEPS = 20;

const SSO_UI_MESSAGES = {
    DEFAULT_LOADING: {
        title: 'Loading Your Property Information',
        message: 'You are being re-directed to Property Pay and this may take a few moments to complete. Please do not close this window.'
    },
    ACCOUNT_NOT_FOUND: {
        title: 'Account Not Found',
        message: 'We have encountered an error loading your Property Pay Account.'
    }
} as const;

@Component({
    selector: 'app-sso-landing',
    template: require('./start-sso-login.component.html')
})
export class StartSsoLoginComponent implements OnInit, OnDestroy {
    statusTitle = SSO_UI_MESSAGES.DEFAULT_LOADING.title;
    statusMessage = SSO_UI_MESSAGES.DEFAULT_LOADING.message;
    progressValue = 0;

    private progressSub!: Subscription;
    private readonly ENV = APP_CONFIG().get_APP_ENV();

    constructor(
        @Inject(OauthService) private oauthService: OauthService,
        @Inject(MatDialog) private dialog: MatDialog,
        @Inject(Router) private router: Router,
        @Inject(ErrorDialogService) private errorDialogService: ErrorDialogService,
        @Inject(HeaderService) private headerService: HeaderService,
    ) { }

    ngOnInit(): void {
        this.headerService.setDisplayRightContent(false);
        this.startProgress();
        this.attemptAuthentication();
    }

    private attemptAuthentication(): void {
        const currentAttempts = Number(sessionStorage.getItem(SSO_LOGIN_ATTEMPTS_KEY) || 0);

        if (currentAttempts >= MAX_LOGIN_ATTEMPTS) {
            console.error(`Max login attempts (${MAX_LOGIN_ATTEMPTS}) reached. Failing.`);
            this.handleFail();
            return; // Stop the process immediately
        }

        sessionStorage.setItem(SSO_LOGIN_ATTEMPTS_KEY, String(currentAttempts + 1));


        setTimeout(() => {
            this.oauthService.authenticate().subscribe({
                error: (err) => {
                    console.error('Authentication initiation failed before redirect.', err);
                    this.handleFail();
                }
            });
        }, AUTH_DELAY_MS);
    }

    private startProgress(): void {
        const INTERVAL_DURATION = AUTH_DELAY_MS / PROGRESS_STEPS;
        const INTERVAL_INCREMENT = PROGRESS_MAX / PROGRESS_STEPS;

        this.progressSub = interval(INTERVAL_DURATION)
            .pipe(take(PROGRESS_STEPS))
            .subscribe({
                next: () => {
                    this.progressValue += INTERVAL_INCREMENT;
                },
                complete: () => {
                    this.progressValue = PROGRESS_MAX;
                },
            });
    }

    private handleFail(): void {
        // Stop the progress bar if it's running.
        if (this.progressSub) {
            this.progressSub.unsubscribe();
        }

        this.errorDialogService.showError(
            SSO_UI_MESSAGES.ACCOUNT_NOT_FOUND.title,
            SSO_UI_MESSAGES.ACCOUNT_NOT_FOUND.message,
            APP_CONSTANTS.CONTACT_INFO
        ).pipe(take(1)).subscribe(() => {
            sessionStorage.clear();
            window.location.href = this.ENV.HOAPASS_WEB_URL;
        })
    }

    ngOnDestroy(): void {
        if (this.progressSub) {
            this.progressSub.unsubscribe();
        }
    }
}
